import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { useApi, useTracker } from "@ferrivia/auth-web";
import { useTranslation } from "@ferrivia/i18n";
import { Text } from "@ferrivia/ui";
import s from "./ScreenerPane.module.css";

/* A questionnaire photo field's ids as "Photo 1 / 2 / 3" buttons. A photo is
   fetched only when clicked -- each fetch is its own käyttöloki row under
   `patient_questionnaire_photos`, so opening the Screener reads none -- and
   shown inside the pane as taken. No download control, no zoom-to-file, and
   the blob URL is revoked on close. Nothing reads the image. */
export function QuestionnairePhotoViewer({ customerId, photoIds }: { customerId: string; photoIds: string[] }) {
  const { t } = useTranslation("clinic");
  const api = useApi();
  const tracker = useTracker();
  const [open, setOpen] = useState<{ position: number; url: string } | null>(null);
  const [failed, setFailed] = useState(false);
  const urlRef = useRef<string | null>(null);

  function release() {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = null;
  }

  useEffect(() => release, []);

  async function show(photoId: string, position: number) {
    setFailed(false);
    try {
      const blob = await api.patients.questionnairePhotoContent(customerId, photoId);
      release();
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      setOpen({ position, url });
      tracker.track("clinic.questionnaire_photo_opened", { patient_id: customerId });
    } catch {
      setFailed(true);
    }
  }

  function close() {
    release();
    setOpen(null);
  }

  const photoLabel = (position: number) => t("screenerPane.photos.photo", "Photo {{number}}", { number: position });

  return (
    <div className={s.photos}>
      <div className={s.photoButtons}>
        {photoIds.map((photoId, index) => (
          <button
            key={photoId}
            type="button"
            className={s.photoButton}
            aria-pressed={open?.position === index + 1}
            onClick={() => void show(photoId, index + 1)}
          >
            {photoLabel(index + 1)}
          </button>
        ))}
      </div>
      {failed ? (
        <Text as="p" size="sm" className={s.helper}>
          {t("screenerPane.photos.openFailed", "Couldn't open the photo.")}
        </Text>
      ) : null}
      {open ? (
        <figure className={s.photoViewer}>
          <img src={open.url} alt={photoLabel(open.position)} className={s.photoImage} draggable={false} />
          <button
            type="button"
            className={s.photoClose}
            onClick={close}
            aria-label={t("screenerPane.photos.close", "Close photo")}
          >
            <X aria-hidden size={16} />
          </button>
        </figure>
      ) : null}
    </div>
  );
}
